/**
 * Display configuration for Details Panel
 * Defines field order, labels, and rendering components for each content type
 */

import {
  IslandHeaders,
  CulturalRegionHeaders,
  MigrationRouteHeaders,
  DiasporaHeaders
} from "@/types/index.ts";

/**
 * Island Display Configuration
 * Defines the order and presentation of island fields
 */
export const ISLAND_DISPLAY_CONFIG = [
  { field: IslandHeaders.NAME, label: null, component: "title" },
  { field: IslandHeaders.INDIGENOUS_NAME, label: null, component: "subtitle" },
  { field: IslandHeaders.DESCRIPTION, label: null, component: "text" },
  { field: IslandHeaders.GEOLOGY, label: "⛰️ Geology", component: "section" },
  { field: IslandHeaders.CLIMATE, label: "🌡️ Climate", component: "section" },
  {
    field: IslandHeaders.CULTURE,
    label: "🪘 Cultural Details",
    component: "section"
  },
  {
    field: IslandHeaders.LINGUISTICS,
    label: "🗣️ Linguistics",
    component: "section"
  },
  { field: IslandHeaders.GENETICS, label: "🧬 Genetics", component: "section" },
  {
    field: IslandHeaders.PHOTOGRAPH,
    label: "📸 Photograph",
    component: "image"
  }
];

/**
 * Cultural Region Display Configuration
 * Defines the order and presentation of cultural region fields
 */
export const REGION_DISPLAY_CONFIG = [
  { field: CulturalRegionHeaders.NAME, label: null, component: "title" },
  { field: CulturalRegionHeaders.DESCRIPTION, label: null, component: "text" },
  {
    field: CulturalRegionHeaders.GENETICS,
    label: "🧬 Genetics",
    component: "section"
  },
  {
    field: CulturalRegionHeaders.CULTURE,
    label: "🪘 Culture",
    component: "section"
  },
  {
    field: CulturalRegionHeaders.LINGUISTICS,
    label: "🗣️ Linguistics",
    component: "section"
  }
];

/**
 * Migration Route Display Configuration
 * Defines the order and presentation of migration route fields
 *
 * Note: Migration routes CSV will eventually have coordinate data for map display.
 * Currently assuming routes can be displayed based on origin/destination text.
 */
export const ROUTE_DISPLAY_CONFIG = [
  {
    field: MigrationRouteHeaders.NAME,
    label: null,
    component: "title"
  },
  {
    field: MigrationRouteHeaders.DATE_RANGE,
    label: "📅 Date Range",
    component: "field"
  },
  {
    field: MigrationRouteHeaders.ORIGIN,
    label: "📍 Origin",
    component: "field"
  },
  {
    field: MigrationRouteHeaders.DESTINATION,
    label: "🎯 Destination",
    component: "field"
  },
  {
    field: MigrationRouteHeaders.COLONIAL_POWER,
    label: "👑 Colonial Power",
    component: "field"
  },
  { field: MigrationRouteHeaders.DESCRIPTION, label: null, component: "text" },
  {
    field: MigrationRouteHeaders.LINGUISTICS,
    label: "🗣️ Linguistic Impact",
    component: "section"
  },
  {
    field: MigrationRouteHeaders.PHOTOGRAPH,
    label: "📸 Photograph",
    component: "image"
  }
];

/**
 * Diaspora Display Configuration
 * Defines the order and presentation of diaspora fields
 *
 * Note: Diaspora destinations are displayed as text labels (no map location data).
 */
export const DIASPORA_DISPLAY_CONFIG = [
  { field: DiasporaHeaders.ORIGIN, label: "📍 Origin", component: "field" },
  {
    field: DiasporaHeaders.DESTINATION,
    label: "🎯 Destination",
    component: "field"
  },
  { field: DiasporaHeaders.DATES, label: "📅 Dates", component: "field" },
  {
    field: DiasporaHeaders.LANGUAGES,
    label: "💬 Languages",
    component: "field"
  },
  {
    field: DiasporaHeaders.CULTURES,
    label: "🌺 Cultures Involved",
    component: "field"
  },
  {
    field: DiasporaHeaders.COLONIAL_POWER,
    label: "👑 Colonial Power",
    component: "field"
  },
  { field: DiasporaHeaders.DESCRIPTION, label: null, component: "text" }
];

/**
 * Get display configuration for a given content type
 * @param {string} type - Content type (island, region, route, diaspora)
 * @returns {Array} Display configuration array
 */
export function getDisplayConfig(type) {
  switch (type) {
    case "island":
      return ISLAND_DISPLAY_CONFIG;
    case "region":
      return REGION_DISPLAY_CONFIG;
    case "route":
      return ROUTE_DISPLAY_CONFIG;
    case "diaspora":
      return DIASPORA_DISPLAY_CONFIG;
    default:
      return [];
  }
}
